// Custom element for the "More Saltbae Faves" PDP recommendations block.
// Fetches Shopify's product recommendations API and renders the result
// through the `sections/pdp-recommendations.liquid` section so we can use
// the bespoke Salt.xo card styling without modifying the theme's shared
// related-products section.
class SaltbaeRecs extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.maxProducts = this.dataset.maxProducts || 3;
    this.intent = this.dataset.intent || 'related';
    this.sectionName = this.dataset.sectionName || 'pdp-recommendations';
    this.container = this.querySelector('[data-products-container]');

    if (!this.productId || !this.container) {
      this.remove();
      return;
    }

    this.bindQuickAdd();
    this.load();
  }

  // Quick add-to-cart for the card CTA. The CTA stays an <a href={PDP}> so it
  // still works with JS disabled and for products that genuinely need the PDP
  // (multi-variant / sold-out) — those render without `data-quick-add`, and we
  // let the click navigate. For a single available variant the Liquid adds
  // `data-quick-add` + `data-variant-id` and we add to the cart instead,
  // mirroring the theme's own product-buy-buttons.js (addItems + drawer.open).
  // Delegated on the root so it also covers the cards injected by load().
  bindQuickAdd() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn || !this.contains(btn)) return;

      const variantId = btn.dataset.variantId;
      const t = window.theme;
      const isDrawerCart = t && t.settings && t.settings.cart_type === 'drawer';

      // Only hijack the link when we can actually add to a drawer cart;
      // otherwise let the <a> navigate to the product page as before.
      if (!variantId || !isDrawerCart || !t.cart || !t.cart.addItems) return;

      e.preventDefault();
      this.quickAdd(btn, variantId);
    });
  }

  async quickAdd(btn, variantId) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');

    const opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ id: variantId, quantity: '1' }).toString(),
    };

    try {
      await window.theme.cart.addItems(opts, variantId);
      const drawer = window.theme.drawer;
      if (drawer && drawer.open) drawer.open('right', 'cart-drawer', btn);
      // Keep the spinner running until a cart is actually on screen, not just
      // until the add resolves. The add is only ~600ms of a ~750ms wait — the
      // drawer still has to open — and clearing early flicks the label back for
      // a moment before anything appears, which reads as a failed click.
      this.clearBusyWhenCartOpens(btn);
    } catch (err) {
      console.error('PDP recommendations quick-add failed:', err);
      btn.setAttribute('aria-busy', 'false');
      // Fall back to the product page so the customer can still add manually.
      if (btn.href) window.location.href = btn.href;
    }
  }

  /**
   * Clears the button's busy state once a cart drawer is visible — OCU's or the
   * theme's, whichever the bridge ends up opening. Capped so the button can
   * never be left spinning: if neither shows within the window, the shopper gets
   * the CTA back and can try again.
   */
  clearBusyWhenCartOpens(btn) {
    const done = () => btn.setAttribute('aria-busy', 'false');
    const deadline = Date.now() + 2500;
    const poll = () => {
      const ocu = document.querySelector('.ocu-cart-drawer.ocu-visible');
      const native = document.querySelector('drawer-root');
      const nativeOpen = native && getComputedStyle(native).display !== 'none';
      if ((ocu && getComputedStyle(ocu).visibility !== 'hidden') || nativeOpen) return done();
      if (Date.now() >= deadline) return done();
      setTimeout(poll, 100);
    };
    poll();
  }

  async load() {
    const base = (window.theme && window.theme.urls && window.theme.urls.product_recommendations)
      ? window.theme.urls.product_recommendations
      : '/recommendations/products';

    const url = `${base}?section_id=${this.sectionName}&product_id=${this.productId}&intent=${this.intent}&limit=${this.maxProducts}`;

    try {
      const res = await fetch(url);
      if (!res.ok) {
        this.remove();
        return;
      }
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newItems = parsed.querySelectorAll('[data-product-item]');

      if (!newItems.length) {
        this.remove();
        return;
      }

      const frag = document.createDocumentFragment();
      newItems.forEach((item) => frag.appendChild(item));
      this.container.innerHTML = '';
      this.container.appendChild(frag);
      this.style.display = 'block';
    } catch (e) {
      console.error('PDP recommendations failed:', e);
      this.remove();
    }
  }
}

if (!customElements.get('saltbae-recs-root')) {
  customElements.define('saltbae-recs-root', SaltbaeRecs);
}
